import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router'

import type { AuthContextValue } from '@/auth/auth-context'
import { AppShell } from '@/components/app-shell/app-shell'
import {
  AdminBranchesPage,
  AdminPage,
  AdminRoleDetailPage,
  AdminRolesPage,
  AdminUserDetailPage,
  AdminUserNewPage,
  AdminUsersPage,
  adminUserListSearchSchema,
} from '@/routes/admin'
import { authSearchSchema } from '@/routes/auth/auth-search'
import { ChangePasswordPage } from '@/routes/auth/change-password'
import { LoginPage } from '@/routes/auth/login'
import { MfaChallengePage } from '@/routes/auth/mfa-challenge'
import { MfaEnrolPage } from '@/routes/auth/mfa-enrol'
import { CustomerDetailPage } from '@/routes/customers/customer-detail'
import { CustomerEditPage } from '@/routes/customers/customer-edit'
import { CustomerListPage } from '@/routes/customers/customer-list'
import { CustomerNewPage } from '@/routes/customers/customer-new'
import { customerDetailSearchSchema, customerListSearchSchema } from '@/routes/customers/search'
import { DashboardPage } from '@/routes/dashboard'
import { PrimitivesPreviewPage } from '@/routes/dev/primitives'
import { GroupDetailPage } from '@/routes/groups/group-detail'
import { GroupListPage } from '@/routes/groups/group-list'
import { MeetingAttendancePage } from '@/routes/groups/meeting-attendance'
import { groupDetailSearchSchema, groupListSearchSchema } from '@/routes/groups/search'
import { ApprovalsPage, approvalsSearchSchema } from '@/routes/approvals'
import {
  CollectionSheetPage,
  CollectionsPage,
  GroupMeetingsPage,
} from '@/routes/collections'
import {
  LedgerAuditPage,
  LedgerBalanceDriftPage,
  LedgerBusinessDaysPage,
  LedgerChartOfAccountsPage,
  LedgerDailyBalancesPage,
  LedgerJournalsPage,
  LedgerManualJournalPage,
  LedgerPage,
  LedgerPostingPeriodsPage,
  LedgerProvisioningPage,
  LedgerTrialBalancePage,
} from '@/routes/ledger'
import {
  ApplyForRoundPage,
  ClassificationSchemeNewPage,
  LoanAccountDetailPage,
  LoanAccountListPage,
  LoanAccountNewPage,
  LoanAppraisalPage,
  LoanApprovalPage,
  LoanClassificationOverridePage,
  LoanCycleDetailPage,
  LoanDisbursementPage,
  LoanDisclosureConfigPage,
  LoanGuaranteeOffsetPage,
  LoanPenaltyWaiverPage,
  LoanPortfolioPage,
  LoanProductDetailPage,
  LoanProductEditPage,
  LoanProductListPage,
  LoanProductNewPage,
  LoanRecoveryPage,
  LoanRepaymentPage,
  LoanRestructurePage,
  LoanSettlementPage,
  LoanWriteOffPage,
} from '@/routes/loans'
import {
  loanAccountDetailSearchSchema,
  loanAccountListSearchSchema,
  loanCycleDetailSearchSchema,
  loanProductListSearchSchema,
} from '@/routes/loans/search'
import {
  CustomerSavingsPage,
  SavingsAccountDetailPage,
  SavingsAccountsPage,
  SavingsCashPage,
  SavingsPeriodEndRunsPage,
  SavingsProductsPage,
} from '@/routes/savings'
import {
  TellerCashPositionPage,
  TellerCounterPage,
  TellerSessionDetailPage,
  TellerSessionListPage,
  TellerSetupPage,
  TillDetailPage,
  VerificationOverridesPage,
} from '@/routes/teller'
import {
  tellerCashPositionSearchSchema,
  tellerSessionDetailSearchSchema,
  tellerSessionListSearchSchema,
  tillDetailSearchSchema,
} from '@/routes/teller/search'
import { DocumentsArchivePage } from '@/routes/documents'
import { NotFoundPage } from '@/routes/not-found'
import { NotificationOutboxPage } from '@/routes/notifications'
import {
  ReportsBalanceSheetPage,
  ReportsCollectionsPage,
  ReportsDisbursementsPage,
  ReportsGroupHealthPage,
  ReportsIncomeStatementPage,
  ReportsPage,
  ReportsParPage,
  ReportsPerformancePage,
  ReportsSnapshotDetailPage,
  ReportsSnapshotsPage,
  ReportsTrialBalancePage,
} from '@/routes/reports'

/**
 * Routes are defined in code rather than generated from the file system: the tree is small, the
 * guards are the interesting part, and having them in one file makes it obvious at a glance which
 * screens sit behind a session and which do not.
 */
export interface RouterContext {
  auth: AuthContextValue
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: NotFoundPage,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: authSearchSchema,
  beforeLoad: ({ context }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({ to: '/' })
    }
  },
  component: LoginPage,
})

const mfaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mfa',
  validateSearch: authSearchSchema,
  beforeLoad: ({ context }) => {
    // The challenge lives in memory only. A reload loses it, and losing it means starting the
    // sign-in again rather than resuming half-way through it.
    if (context.auth.challenge?.kind !== 'totp') {
      throw redirect({ to: '/login' })
    }
  },
  component: MfaChallengePage,
})

const mfaEnrolRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mfa/enrol',
  validateSearch: authSearchSchema,
  beforeLoad: ({ context }) => {
    // Same memory-only rule as /mfa: a reload loses the challenge, and with it the attempt.
    if (context.auth.challenge?.kind !== 'mfa_enrolment') {
      throw redirect({ to: '/login' })
    }
  },
  component: MfaEnrolPage,
})

const changePasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/password/change',
  validateSearch: authSearchSchema,
  beforeLoad: ({ context }) => {
    const forced = context.auth.challenge?.kind === 'password_change'
    if (!forced && !context.auth.isAuthenticated) {
      throw redirect({ to: '/login' })
    }
  },
  component: ChangePasswordPage,
})

/**
 * Everything below this layout requires a session. The guard is a convenience for the operator,
 * not a security boundary: the server authorises every request on its own, so the worst a
 * bypassed guard produces is an empty screen full of 401s.
 */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  beforeLoad: ({ context, location }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login', search: { redirect: location.href } })
    }
  },
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
})

const dashboardRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: DashboardPage })

/**
 * The customer screens. `/customers/new` is declared before the parameterised route only for
 * readability — a static segment outranks a dynamic one whatever order they are written in.
 */
const customersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/customers',
  validateSearch: customerListSearchSchema,
  component: CustomerListPage,
})

const customerNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/customers/new',
  component: CustomerNewPage,
})

const customerDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/customers/$customerId',
  validateSearch: customerDetailSearchSchema,
  component: CustomerDetailPage,
})

const customerEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/customers/$customerId/edit',
  component: CustomerEditPage,
})

/**
 * The group screens. Attendance hangs off the meeting rather than the group in the URL as well as
 * in the API, because that is what it belongs to — and because a register's link has to keep
 * meaning the same register when the group's calendar moves around it.
 */
const groupsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/groups',
  validateSearch: groupListSearchSchema,
  component: GroupListPage,
})

const groupDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/groups/$groupId',
  validateSearch: groupDetailSearchSchema,
  component: GroupDetailPage,
})

const meetingAttendanceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/groups/$groupId/meetings/$meetingId',
  component: MeetingAttendancePage,
})

/**
 * The loan screens. A loan account is permanent and its number never changes; each round is a new
 * cycle on it, which is why every cycle operation hangs off `/loans/$accountId/cycles/$cycleId`
 * rather than off a route of its own. Approval and disbursement are separate paths because they are
 * separate signatures — three eyes minimum on money leaving the institution.
 */

const loansRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans',
  validateSearch: loanAccountListSearchSchema,
  component: LoanAccountListPage,
})

const loansNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/new',
  component: LoanAccountNewPage,
})

const loansProductsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/products',
  validateSearch: loanProductListSearchSchema,
  component: LoanProductListPage,
})

const loansProductsNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/products/new',
  component: LoanProductNewPage,
})

const loansProductsProductidRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/products/$productId',
  component: LoanProductDetailPage,
})

const loansProductsProductidEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/products/$productId/edit',
  component: LoanProductEditPage,
})

const loansPortfolioRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/portfolio',
  component: LoanPortfolioPage,
})

const loansClassificationSchemesNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/classification-schemes/new',
  component: ClassificationSchemeNewPage,
})

const loansDisclosureConfigRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/disclosure-config',
  component: LoanDisclosureConfigPage,
})

const loansAccountidRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/$accountId',
  validateSearch: loanAccountDetailSearchSchema,
  component: LoanAccountDetailPage,
})

const loansAccountidApplyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/$accountId/apply',
  component: ApplyForRoundPage,
})

const loansAccountidCyclesCycleidRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/$accountId/cycles/$cycleId',
  validateSearch: loanCycleDetailSearchSchema,
  component: LoanCycleDetailPage,
})

const loansAccountidCyclesCycleidAppraiseRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/$accountId/cycles/$cycleId/appraise',
  component: LoanAppraisalPage,
})

const loansAccountidCyclesCycleidApproveRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/$accountId/cycles/$cycleId/approve',
  component: LoanApprovalPage,
})

const loansAccountidCyclesCycleidDisburseRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/$accountId/cycles/$cycleId/disburse',
  component: LoanDisbursementPage,
})

const loansAccountidCyclesCycleidRepayRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/$accountId/cycles/$cycleId/repay',
  component: LoanRepaymentPage,
})

const loansAccountidCyclesCycleidSettleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/$accountId/cycles/$cycleId/settle',
  component: LoanSettlementPage,
})

const loansAccountidCyclesCycleidWaiveRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/$accountId/cycles/$cycleId/waive',
  component: LoanPenaltyWaiverPage,
})

const loansAccountidCyclesCycleidRestructureRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/$accountId/cycles/$cycleId/restructure',
  component: LoanRestructurePage,
})

const loansAccountidCyclesCycleidWriteOffRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/$accountId/cycles/$cycleId/write-off',
  component: LoanWriteOffPage,
})

const loansAccountidCyclesCycleidOffsetRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/$accountId/cycles/$cycleId/offset',
  component: LoanGuaranteeOffsetPage,
})

const loansAccountidCyclesCycleidRecoveryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/$accountId/cycles/$cycleId/recovery',
  component: LoanRecoveryPage,
})

const loansAccountidCyclesCycleidClassifyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/loans/$accountId/cycles/$cycleId/classify',
  component: LoanClassificationOverridePage,
})



/**
 * The savings screens, covering both product classes. A security deposit is a separate account with
 * its own GL liability and no withdrawals, so it shares these routes but not every action on them.
 */

const savingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/savings',
  component: SavingsAccountsPage,
})

const savingsAccountsAccountidRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/savings/accounts/$accountId',
  component: SavingsAccountDetailPage,
})

const savingsAccountsAccountidCashRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/savings/accounts/$accountId/cash',
  component: SavingsCashPage,
})

const savingsCustomersCustomeridRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/savings/customers/$customerId',
  component: CustomerSavingsPage,
})

const savingsProductsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/savings/products',
  component: SavingsProductsPage,
})

const savingsRunsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/savings/runs',
  component: SavingsPeriodEndRunsPage,
})



/**
 * The teller workstation, which is till-first: `/teller` is the drawer you are working, not a menu.
 * Everything that moves cash refuses below the large breakpoint — keying an amount is what touch is
 * worst at, and a mis-keyed deposit is a customer's money in the wrong place.
 */

const tellerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/teller',
  component: TellerCounterPage,
})

const tellerCashRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/teller/cash',
  validateSearch: tellerCashPositionSearchSchema,
  component: TellerCashPositionPage,
})

const tellerSessionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/teller/sessions',
  validateSearch: tellerSessionListSearchSchema,
  component: TellerSessionListPage,
})

const tellerSessionsSessionidRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/teller/sessions/$sessionId',
  validateSearch: tellerSessionDetailSearchSchema,
  component: TellerSessionDetailPage,
})

const tellerTillsTillidRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/teller/tills/$tillId',
  validateSearch: tillDetailSearchSchema,
  component: TillDetailPage,
})

const tellerSetupRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/teller/setup',
  component: TellerSetupPage,
})

const tellerVerificationOverridesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/teller/verification-overrides',
  component: VerificationOverridesPage,
})



/**
 * The general ledger. Journals are immutable here as they are in the database: corrections are
 * reversals, and no screen offers an edit the server would refuse.
 */

const ledgerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/ledger',
  component: LedgerPage,
})

const ledgerBusinessDaysRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/ledger/business-days',
  component: LedgerBusinessDaysPage,
})

const ledgerProvisioningRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/ledger/provisioning',
  component: LedgerProvisioningPage,
})

const ledgerJournalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/ledger/journals',
  component: LedgerJournalsPage,
})

/**
 * Raising a journal by hand is the most abusable operation in a core banking system, so it is its
 * own route rather than a dialog on the list — countersigned, and refusing below the large
 * breakpoint because it is where somebody keys debits and credits.
 */
const ledgerManualJournalRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/ledger/journals/new',
  component: LedgerManualJournalPage,
})

const ledgerChartOfAccountsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/ledger/accounts',
  component: LedgerChartOfAccountsPage,
})

const ledgerTrialBalanceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/ledger/trial-balance',
  component: LedgerTrialBalancePage,
})

const ledgerDailyBalancesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/ledger/daily-balances',
  component: LedgerDailyBalancesPage,
})

const ledgerPostingPeriodsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/ledger/posting-periods',
  component: LedgerPostingPeriodsPage,
})

/** An unresolved drift alarm blocks the branch's day close, so this is where the close gets unblocked. */
const ledgerBalanceDriftRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/ledger/balance-drift',
  component: LedgerBalanceDriftPage,
})

const ledgerAuditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/ledger/audit',
  component: LedgerAuditPage,
})



/**
 * What the institution has sent, is still trying to send, and decided not to. A read screen that
 * adapts on a phone: the call it answers — "was my customer told?" — is as likely to be taken
 * standing up as at a desk, and nothing on it keys an amount.
 */
const notificationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/notifications',
  component: NotificationOutboxPage,
})

/**
 * The archive of generated paper — the folder a kiosk counter opens to reprint. Adapts on a phone:
 * the viewer falls back to "save a copy" where a PDF cannot be shown inline.
 */
const documentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/documents',
  component: DocumentsArchivePage,
})

/** The maker-checker inbox. What a checker is countersigning, in domain terms rather than raw JSON. */

const approvalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/approvals',
  validateSearch: approvalsSearchSchema,
  component: ApprovalsPage,
})



/**
 * Bulk collection capture — the paper workflow, and the highest-volume screen in the institution
 * until the field application ships. A meeting's sheet is transcribed here in one reviewed batch.
 */

const collectionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/collections',
  component: CollectionsPage,
})

const collectionsGroupidRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/collections/$groupId',
  component: GroupMeetingsPage,
})

const collectionsGroupidMeetingidRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/collections/$groupId/$meetingId',
  component: CollectionSheetPage,
})


/**
 * The reporting module. Every screen here is a read: producing a report is not the same as keying
 * one, so nothing below refuses on width — see `docs/mobile-policy.md`'s general split, the same
 * one the ledger's own read screens follow.
 */

const reportsRoute = createRoute({ getParentRoute: () => appRoute, path: '/reports', component: ReportsPage })

const reportsParRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/par',
  component: ReportsParPage,
})

const reportsCollectionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/collections',
  component: ReportsCollectionsPage,
})

const reportsDisbursementsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/disbursements',
  component: ReportsDisbursementsPage,
})

const reportsPerformanceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/performance',
  component: ReportsPerformancePage,
})

const reportsGroupHealthRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/group-health',
  component: ReportsGroupHealthPage,
})

const reportsTrialBalanceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/trial-balance',
  component: ReportsTrialBalancePage,
})

const reportsBalanceSheetRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/balance-sheet',
  component: ReportsBalanceSheetPage,
})

const reportsIncomeStatementRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/income-statement',
  component: ReportsIncomeStatementPage,
})

/**
 * Building a snapshot is a form on the list screen rather than its own route — there is no
 * `/reports/snapshots/new` — so only these two exist: the list-and-build screen, and the detail
 * screen for one already-frozen snapshot.
 */
const reportsSnapshotsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/snapshots',
  component: ReportsSnapshotsPage,
})

const reportsSnapshotDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/snapshots/$snapshotId',
  component: ReportsSnapshotDetailPage,
})

/**
 * Administration. Users and roles are dual-controlled throughout — see `features/admin/dual-control.tsx`
 * — and every screen refuses below `lg`, the same "rarely urgent, permanently consequential" call
 * `AdminPage` itself makes for the whole module.
 */
const adminRoute = createRoute({ getParentRoute: () => appRoute, path: '/admin', component: AdminPage })

const adminUsersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/users',
  validateSearch: adminUserListSearchSchema,
  component: AdminUsersPage,
})

const adminUsersNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/users/new',
  component: AdminUserNewPage,
})

const adminUsersUseridRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/users/$userId',
  component: AdminUserDetailPage,
})

const adminRolesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/roles',
  component: AdminRolesPage,
})

const adminRolesRoleidRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/roles/$roleId',
  component: AdminRoleDetailPage,
})

const adminBranchesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/branches',
  component: AdminBranchesPage,
})

/** Component preview. Reachable in a development build only — see the page's own note. */
const primitivesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/dev/primitives',
  beforeLoad: () => {
    if (!import.meta.env.DEV) {
      throw redirect({ to: '/' })
    }
  },
  component: PrimitivesPreviewPage,
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  mfaRoute,
  mfaEnrolRoute,
  changePasswordRoute,
  appRoute.addChildren([
    dashboardRoute,
    customersRoute,
    customerNewRoute,
    customerDetailRoute,
    customerEditRoute,
    groupsRoute,
    groupDetailRoute,
    meetingAttendanceRoute,
    loansRoute,
    loansNewRoute,
    loansProductsRoute,
    loansProductsNewRoute,
    loansProductsProductidRoute,
    loansProductsProductidEditRoute,
    loansPortfolioRoute,
    loansClassificationSchemesNewRoute,
    loansDisclosureConfigRoute,
    loansAccountidRoute,
    loansAccountidApplyRoute,
    loansAccountidCyclesCycleidRoute,
    loansAccountidCyclesCycleidAppraiseRoute,
    loansAccountidCyclesCycleidApproveRoute,
    loansAccountidCyclesCycleidDisburseRoute,
    loansAccountidCyclesCycleidRepayRoute,
    loansAccountidCyclesCycleidSettleRoute,
    loansAccountidCyclesCycleidWaiveRoute,
    loansAccountidCyclesCycleidRestructureRoute,
    loansAccountidCyclesCycleidWriteOffRoute,
    loansAccountidCyclesCycleidOffsetRoute,
    loansAccountidCyclesCycleidRecoveryRoute,
    loansAccountidCyclesCycleidClassifyRoute,
    savingsRoute,
    savingsAccountsAccountidRoute,
    savingsAccountsAccountidCashRoute,
    savingsCustomersCustomeridRoute,
    savingsProductsRoute,
    savingsRunsRoute,
    tellerRoute,
    tellerCashRoute,
    tellerSessionsRoute,
    tellerSessionsSessionidRoute,
    tellerTillsTillidRoute,
    tellerSetupRoute,
    tellerVerificationOverridesRoute,
    ledgerRoute,
    ledgerBusinessDaysRoute,
    ledgerProvisioningRoute,
    ledgerJournalsRoute,
    ledgerManualJournalRoute,
    ledgerChartOfAccountsRoute,
    ledgerTrialBalanceRoute,
    ledgerDailyBalancesRoute,
    ledgerPostingPeriodsRoute,
    ledgerBalanceDriftRoute,
    ledgerAuditRoute,
    approvalsRoute,
    notificationsRoute,
    documentsRoute,
    collectionsRoute,
    collectionsGroupidRoute,
    collectionsGroupidMeetingidRoute,
    reportsRoute,
    reportsParRoute,
    reportsCollectionsRoute,
    reportsDisbursementsRoute,
    reportsPerformanceRoute,
    reportsGroupHealthRoute,
    reportsTrialBalanceRoute,
    reportsBalanceSheetRoute,
    reportsIncomeStatementRoute,
    reportsSnapshotsRoute,
    reportsSnapshotDetailRoute,
    adminRoute,
    adminUsersRoute,
    adminUsersNewRoute,
    adminUsersUseridRoute,
    adminRolesRoute,
    adminRolesRoleidRoute,
    adminBranchesRoute,
    primitivesRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  // Supplied for real by <RouterProvider context={{ auth }}> once the session is resolved.
  context: { auth: undefined! },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  defaultNotFoundComponent: NotFoundPage,
  scrollRestoration: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
